import { logError } from '@ptek/client-logger';
import { useWidgetEvents } from '@ptek/widget-core';
import { useCallback, useContext } from 'react';
import {
  contestEntriesClient,
  type CreateContestEntryResponse,
} from '../../../api-clients/contestEntries';
import { WIDGET_EVENTS } from '../../../constants/events';
import { AppContext } from '../../../context/AppContext';
import { useUserpilotTracking } from '../../../hooks/useUserpilotTracking';
import { useRecordingStore } from '../../../stores';
import { DocumentState } from '../../../types/document';
import { TRACK_EVENTS } from '../../../userpilot';
import { TranscriptProcessor } from '../../../utils/transcriptUtils';

interface UseDocumentExportProps {
  saveCurrentEdits: () => Promise<void>;
}

interface UseDocumentExportReturn {
  handleExport: (
    documentState: DocumentState,
    exportAction: string
  ) => Promise<CreateContestEntryResponse | null>;
}

const exportUtils = {
  getSessionMetadata(
    activeRecordingSession: {
      encounterId?: string;
      id?: string;
      patientName?: string;
    } | null
  ) {
    const encounterId =
      activeRecordingSession?.encounterId ||
      activeRecordingSession?.id ||
      'unknown';
    const sessionId = activeRecordingSession?.id || encounterId;

    return {
      encounterId,
      sessionId,
      patientName: activeRecordingSession?.patientName || 'Unknown Patient',
    };
  },

  prepareExportContent(documentState: DocumentState) {
    return documentState.content;
  },

  buildExportData(
    documentState: DocumentState,
    sessionMetadata: ReturnType<typeof exportUtils.getSessionMetadata>,
    exportAction: string,
    transcriptText?: string
  ) {
    const exportContent = exportUtils.prepareExportContent(documentState);

    return {
      document: {
        id: documentState.id,
        content: exportContent,
        ...sessionMetadata,
        type: 'chart_note' as const,
        documentType: 'SOAP_NOTE',
      },
      exportAction,
      transcriptText,
      widgetState: {
        currentContent: documentState.content,
        isModified: documentState.isModified,
        timestamp: Date.now(),
      },
    };
  },
};

export function useDocumentExport({
  saveCurrentEdits,
}: UseDocumentExportProps): UseDocumentExportReturn {
  const { emitToHost } = useWidgetEvents();
  const { activeRecordingSession, savedTranscriptData } = useRecordingStore();
  const { trackEvent } = useUserpilotTracking();
  const { sessionModel, contestStatus } = useContext(AppContext);

  const handleExport = useCallback(
    async (documentState: DocumentState, exportAction: string) => {
      if (!documentState) {
        return null;
      }

      try {
        await saveCurrentEdits();

        const sessionMetadata = exportUtils.getSessionMetadata(
          activeRecordingSession
        );
        const transcriptText =
          TranscriptProcessor.toPlainText(sessionModel.transcript) ??
          savedTranscriptData?.transcriptText;
        const exportData = exportUtils.buildExportData(
          documentState,
          sessionMetadata,
          exportAction,
          transcriptText
        );

        emitToHost(WIDGET_EVENTS.EMITTER_NAMES.EXPORT_DOCUMENT, exportData);

        let contestEntryResponse: CreateContestEntryResponse | null = null;
        const isContestEligible =
          contestStatus.contestActive && contestStatus.tabVisible;

        if (isContestEligible) {
          try {
            contestEntryResponse = await contestEntriesClient.createContestEntry(
              sessionMetadata.sessionId
            );
          } catch (error) {
            logError(
              'useDocumentExport',
              'Failed to create contest entry after export:',
              error
            );
          }
        }

        trackEvent(TRACK_EVENTS.DOCUMENT_EXPORTED, {
          documentId: documentState.id,
          templateId: documentState.templateId,
          templateName: documentState.templateName,
          sessionId: sessionMetadata?.sessionId,
          documentLength: JSON.stringify(documentState.content).length,
        });

        return contestEntryResponse;
      } catch (error) {
        logError('useDocumentExport', 'Failed to export document:', error);
        return null;
      }
    },
    [
      saveCurrentEdits,
      emitToHost,
      activeRecordingSession,
      sessionModel.transcript,
      savedTranscriptData,
      trackEvent,
      contestStatus,
    ]
  );

  return {
    handleExport,
  };
}
